import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Divider, Input, Radio } from 'antd'
import ConfirmationModal, { ModalDescription } from './ConfirmationModal'
import {
  OutstandingInvoicesModal,
  OUTSTANDING_INVOICES_ERROR
} from './OutstandingInvoicesModal'
import { useMessageStore } from 'hooks/useGlobalContext/useMessageStore'
import { useSession } from 'packages/@slerp/accounts'

interface UseStripeReconnectArgs {
  reconnect: (stripeAccountId: string) => Promise<unknown>
  onSuccess: () => void
  invoicesPath: string
  previousAccounts: string[]
  hasConnected: boolean
}

export const useStripeReconnect = ({
  reconnect,
  onSuccess,
  invoicesPath,
  previousAccounts,
  hasConnected
}: UseStripeReconnectArgs) => {
  const navigate = useNavigate()
  const { user } = useSession()
  const { addMessage } = useMessageStore()
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const [isOutstandingInvoicesOpen, setIsOutstandingInvoicesOpen] =
    useState(false)
  const [reconnecting, setReconnecting] = useState(false)
  const [accountId, setAccountId] = useState('')

  // The backend authorizes reconnect on the DB `role`, not the `hasuraRole` JWT
  // claim `isAdmin()` reads - keep this check on `user.role` to match it.
  const isSuperAdmin = user?.role === 'superadmin'
  const canReconnect =
    user?.role === 'admin' || isSuperAdmin
      ? isSuperAdmin || previousAccounts.length > 0
      : false

  const openReconnect = () => {
    setAccountId('')
    setIsConfirmOpen(true)
  }

  const handleConfirm = async () => {
    if (!accountId) {
      return
    }

    setReconnecting(true)

    try {
      await reconnect(accountId)
      setIsConfirmOpen(false)
      addMessage({ type: 'message', content: 'Stripe account reconnected' })
      onSuccess()
    } catch (error) {
      setIsConfirmOpen(false)
      const errorMessage = error instanceof Error ? error.message : ''

      if (errorMessage === OUTSTANDING_INVOICES_ERROR) {
        setIsOutstandingInvoicesOpen(true)
      } else if (errorMessage === 'Unauthorized') {
        addMessage({
          type: 'error',
          content: 'You are not authorized to reconnect this Stripe account'
        })
      } else {
        addMessage({
          type: 'error',
          content:
            errorMessage ||
            'Failed to reconnect Stripe account. Please try again'
        })
      }
    } finally {
      setReconnecting(false)
    }
  }

  const modals = (
    <>
      <ConfirmationModal
        title={
          hasConnected
            ? 'Switch to a different Stripe account'
            : 'Reconnect a previous Stripe account'
        }
        confirmLabel='Reconnect'
        confirmVariant='default'
        isOpen={isConfirmOpen}
        onOk={handleConfirm}
        onCancel={() => setIsConfirmOpen(false)}
        confirmLoading={reconnecting}
        confirmDisabled={!accountId}
      >
        <ModalDescription>
          {previousAccounts.length > 0
            ? 'Choose a previously connected account to reconnect.'
            : 'Enter the Stripe account ID to connect.'}
        </ModalDescription>
        {previousAccounts.length > 0 && (
          <Radio.Group
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
          >
            {previousAccounts.map((previousAccountId) => (
              <Radio key={previousAccountId} value={previousAccountId}>
                {previousAccountId}
              </Radio>
            ))}
          </Radio.Group>
        )}
        {isSuperAdmin && (
          <>
            <Divider />
            <ModalDescription>Or enter an account ID</ModalDescription>
            <Input
              placeholder='acct_...'
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
            />
          </>
        )}
      </ConfirmationModal>
      <OutstandingInvoicesModal
        isOpen={isOutstandingInvoicesOpen}
        onGoToInvoices={() => {
          setIsOutstandingInvoicesOpen(false)
          navigate(invoicesPath)
        }}
        onCancel={() => setIsOutstandingInvoicesOpen(false)}
      />
    </>
  )

  return { modals, openReconnect, reconnecting, canReconnect }
}
